import { consume } from "@lit/context";
import {
    clone,
    type Employee,
    PASSWORD_MIN_LENGTH,
    PASSWORD_REQUIREMENT_HINT,
    SetPasswordParams,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { html, LitElement, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import OneSignal from "react-onesignal";
import "../../components/avatar/avatar";
import { enabledFeatures, type EnabledFeatures } from "../../contexts/features-context";
import { app } from "../../init";
import { validateNewPasswordInputs } from "../../lib/password-input";
import { singleton } from "../../lib/singleton";
import { routeProperty, Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import "../capture-image";
import type { CaptureImage } from "../capture-image";
import { Checkbox } from "../checkbox";
import "../scroller";
import { push } from "./push";

@customElement("ptc-staff-settings")
export class StaffSettings extends Routing(StateMixin(LitElement)) {
    static pages = ["account", "notifications"];

    readonly routePattern = /^settings(?:\/(?<page>[^/]+))?/;

    @consume({ context: enabledFeatures, subscribe: true })
    features: EnabledFeatures;

    get routeTitle() {
        return "Einstellungen";
    }

    get hasChanges() {
        if (!this._data || !app.account || !app.profile) {
            return false;
        }

        if (this._updatingPassword) {
            return true;
        }

        const { avatar, email } = this._data;
        const emailChanged = email !== (app.account.email || "");
        const avatarChanged = avatar !== app.profile.avatar;

        return emailChanged || avatarChanged;
    }

    clearChanges(): void {
        if (app.profile && app.account) {
            this._profile = clone(app.profile);
            this._emailInput.value = app.account.email || "";
            if (this._oldPasswordInput) {
                this._oldPasswordInput.value = "";
            }
            if (this._passwordInput) {
                this._passwordInput.value = "";
            }
            if (this._repeatPasswordInput) {
                this._repeatPasswordInput.value = "";
            }
            this._updatingPassword = false;
            this.requestUpdate();
        }
    }

    @state()
    private _loading = false;

    @state()
    private _profile: Employee;

    @routeProperty({ arg: "page" })
    private _page: string;

    @state()
    private _updatingPassword = false;

    @singleton("ptc-capture-image")
    private _captureImage: CaptureImage;

    @query("form")
    private _form: HTMLFormElement;

    @query("input[name='email']")
    private _emailInput: HTMLInputElement;

    @query("input[name='oldPassword']")
    private _oldPasswordInput: HTMLInputElement;

    @query("input[name='password']")
    private _passwordInput: HTMLInputElement;

    @query("input[name='repeatPassword']")
    private _repeatPasswordInput: HTMLInputElement;

    private get _data() {
        if (!this._form) {
            return null;
        }
        const data = new FormData(this._form);
        const email = data.get("email") as string;
        const avatar = data.get("avatar") as string;
        const oldPassword = data.get("oldPassword") as string;
        const password = data.get("password") as string;
        const repeatPassword = data.get("repeatPassword") as string;
        return { email, avatar, oldPassword, password, repeatPassword };
    }

    async handleRoute() {
        if (!StaffSettings.pages.includes(this._page)) {
            this.redirect("settings/account");
            return;
        }
        if (app.profile) {
            this._profile = clone(app.profile);
            await this.updateComplete;
        }
        if (this._updatingPassword) {
            // if email is already set, focus the password input, otherwise the email input
            setTimeout(() => (app.account?.email ? this._passwordInput.focus() : this._emailInput.focus()), 500);
        }
    }

    private async _editAvatar() {
        const avatar = await this._captureImage.show();
        if (avatar) {
            this._profile = Object.assign(clone(this._profile), { avatar });
            await this.updateComplete;
            this.requestUpdate();
        }
    }

    private async _deleteAvatar() {
        if (
            !(await confirm("Willst du dein Profilbild wirklich löschen", "Löschen", "Abbrechen", {
                title: "Profilbild Löschen",
                type: "destructive",
            }))
        ) {
            return;
        }

        this._profile = Object.assign(clone(this._profile), { avatar: "" });
        await this.updateComplete;
        this.requestUpdate();
    }

    private async _submitAccountSettings(e: Event) {
        e.preventDefault();

        if (!this._data) {
            return;
        }

        const { avatar, email, oldPassword, password } = this._data;

        const hadPassword = app.account!.hasPassword;
        this._loading = true;

        try {
            const profile: Partial<Employee> = {};
            if (avatar !== app.profile!.avatar) {
                profile.avatar = avatar;
            }
            await app.updateAccount({
                email,
                profile,
            });
            if (password) {
                await app.api.setPassword(
                    new SetPasswordParams({
                        id: app.account!.id,
                        password,
                        oldPassword: hadPassword ? oldPassword : undefined,
                    })
                );
                if (hadPassword) {
                    void alert("Passwort erfolgreich geändert!", { title: "Passwort Geändert", type: "success" });
                } else {
                    void alert(
                        "Passwortzugang erfolgreich eingerichtet! Du kannst dich jetzt jederzeit mit deiner Email und deinem neuen Passwort in der Mitarbeiterapp anmelden!",
                        { title: "Passwortzugang Eingerichtet", type: "success" }
                    );
                }
            }
            this._updatingPassword = false;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
        await this.handleRoute();
    }

    private async _logout() {
        const confirmed = await confirm("Bist du sicher dass Du dich ausloggen möchtest?", "Ausloggen", "Abbrechen", {
            title: "Ausloggen",
            icon: "icon.action.log-out",
        });
        if (confirmed) {
            // performance.mark("logout-started");
            if (OneSignal.User.onesignalId) {
                // note: logout seems to take a while, so we don't wait for it
                OneSignal.logout().catch((error) => {
                    push.debug("OneSignal logout failed:", error);
                });
            }
            // performance.mark("logout-finished");
            await app.logout().catch((error) => {
                console.error("App logout failed:", error);
            });
            // push.debug(
            //     "logout-duration",
            //     performance.measure("logout-duration", "logout-started", "logout-finished").duration
            // );
            window.location.href = "/";
        }
    }

    private _checkPasswordInputs() {
        if (!this._passwordInput || !this._repeatPasswordInput) {
            return;
        }
        validateNewPasswordInputs(this._passwordInput, this._repeatPasswordInput, {
            email: this._emailInput?.value,
            firstName: app.account?.firstName,
            lastName: app.account?.lastName,
            companyName: app.company?.name,
        });
    }

    static styles = [shared, Checkbox.styles];

    render() {
        if (!app.account || !this._profile) {
            return;
        }

        return html`
            <div class="fullbleed vertical layout">
                <header class="bigger padded center-aligning horizontal layout">
                    <button class="small transparent" @click=${() => this.go("home")}>
                        <ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon>
                    </button>
                    <div class="larger left-margined stretch small-caps semibold">Einstellungen</div>
                    <button class="small subtle" @click=${this._logout}>
                        <ptc-ui-icon name="icon.action.log-out"></ptc-ui-icon>
                        Abmelden
                    </button>
                </header>

                <ptc-scroller class="stretch">
                    <form @submit=${this._submitAccountSettings} @change=${() => this.requestUpdate()}>
                        <div class="margined box">
                            <button
                                type="button"
                                class="larger small-caps fill-horizontally transparent text-left-aligning"
                                @click=${() => this.go("settings/account")}
                            >
                                <div class="horizontal center-aligning layout">
                                    <div class="stretch">Account</div>
                                    ${this._page === "account"
                                        ? html`<ptc-ui-icon
                                              name="icon.toggle.expand-active"
                                              class="smaller"
                                          ></ptc-ui-icon>`
                                        : html`<ptc-ui-icon name="icon.toggle.expand" class="smaller"></ptc-ui-icon>`}
                                </div>
                            </button>
                            <ptc-ui-collapse .collapsed=${this._page !== "account"}>
                                <div class="padded spacing vertical layout">
                                    <div>
                                        <label>Profilbild</label>
                                        <div class="horizontally-padded horizontal spacing center-aligning layout">
                                            <ptc-avatar .employee=${this._profile} class="huge"></ptc-avatar>

                                            <input type="hidden" name="avatar" .value=${this._profile.avatar} />

                                            <button type="button" @click=${this._editAvatar} class="stretch">
                                                <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                Bild Wählen
                                            </button>

                                            <button
                                                type="button"
                                                class="negative stretch"
                                                @click=${this._deleteAvatar}
                                                ?hidden=${!this._profile.avatar}
                                            >
                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                Löschen
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label>PIN</label>
                                        <input
                                            type="text"
                                            name="timePin"
                                            value=${this._profile.timePin || ""}
                                            readonly
                                            inputmode="numeric"
                                            class="fill-horizontally"
                                        />
                                    </div>

                                    <div>
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Emailadresse"
                                            .value=${app.account.email || ""}
                                            ?required=${this._updatingPassword}
                                            class="fill-horizontally"
                                        />
                                    </div>

                                    ${this._renderPushNotificationSettings()}
                                    ${this._updatingPassword
                                        ? html`
                                              ${app.account.hasPassword
                                                  ? html`
                                                        <div>
                                                            <label>Passwort</label>
                                                            <input
                                                                type="password"
                                                                name="oldPassword"
                                                                placeholder="Aktuelles Passwort"
                                                                required
                                                                class="fill-horizontally"
                                                            />
                                                        </div>
                                                    `
                                                  : nothing}
                                              <div>
                                                  <label>Neues Passwort</label>
                                                  <input
                                                      type="password"
                                                      name="password"
                                                      placeholder="Neues Passwort Eingeben"
                                                      required
                                                      minlength=${PASSWORD_MIN_LENGTH}
                                                      @input=${this._checkPasswordInputs}
                                                      class="fill-horizontally"
                                                  />
                                                  <div class="small subtle">${PASSWORD_REQUIREMENT_HINT}</div>
                                              </div>
                                              <div>
                                                  <label>Neues Passwort Wiederholen</label>
                                                  <input
                                                      type="password"
                                                      name="repeatPassword"
                                                      placeholder="Neues Passwort Wiederholen"
                                                      required
                                                      @input=${this._checkPasswordInputs}
                                                      class="fill-horizontally"
                                                  />
                                              </div>
                                          `
                                        : html`
                                              <div>
                                                  <label>Passwort</label>
                                                  <div class="spacing vertical layout">
                                                      <div
                                                          class="horizontally-padded"
                                                          ?hidden=${app.account.hasPassword}
                                                      >
                                                          Richte jetzt einen Passwortzugang ein, damit du dich jederzeit
                                                          und von Überall anmelden kannst!
                                                      </div>
                                                      <button
                                                          type="button"
                                                          @click=${() => (this._updatingPassword = true)}
                                                      >
                                                          ${app.account.hasPassword
                                                              ? "Passwort Ändern"
                                                              : "Passwortzugang Einrichten"}
                                                      </button>
                                                  </div>
                                              </div>
                                          `}
                                    ${this.hasChanges
                                        ? html`
                                              <div class="horizontal spacing layout">
                                                  <button class="primary stretch">Speichern</button>
                                                  <button type="button" class="stretch" @click="${this.clearChanges}">
                                                      Verwerfen
                                                  </button>
                                              </div>
                                          `
                                        : ""}
                                </div>
                            </ptc-ui-collapse>
                        </div>
                    </form>
                </ptc-scroller>
            </div>

            <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }

    private _renderPushNotificationSettings() {
        return html`
            <div>
                <div class="vertical spacing layout">
                    ${!push.isSubscribed
                        ? //consent is explicitly not given
                          html`<label>Push-Benachrichtigungen werden <u>nicht</u> an dieses Gerät zugestellt.</label>
                              <button
                                  type="button"
                                  class="primary subtle stretch"
                                  @click="${async () => {
                                      // if somehow consent is not given or indeterminate, ask for again
                                      if (app.state.oneSignalConsentGiven === null) {
                                          await push.askForPushPermission();
                                      } else {
                                          await push.setSubscription(true);
                                      }

                                      this.requestUpdate("state");
                                  }}"
                              >
                                  <ptc-ui-icon name="icon.status.success"></ptc-ui-icon>
                                  Nachrichten erhalten
                              </button>`
                        : nothing}
                    ${push.isSubscribed
                        ? //consent is given
                          html`<label>Push-Benachrichtigungen werden an dieses Gerät zugestellt.</label>
                              <button
                                  type="button"
                                  class="red subtle stretch"
                                  @click=${async () => {
                                      await push.setSubscription(false);
                                      this.requestUpdate("state");
                                  }}
                              >
                                  <ptc-ui-icon name="icon.action.remove"></ptc-ui-icon>
                                  Nachrichten nicht mehr erhalten
                              </button>`
                        : nothing}
                </div>
            </div>
        `;
    }
}
